import type {
  EmailProviderVariant,
  EmailSecurityType,
  ProviderStatus,
  ProviderFormValues,
  WhatsAppProviderType,
  TeamChatPlatformKey,
  TeamChatEntryForm,
} from './types/provider';

/**
 * Team-chat platforms. `key` matches the API's camelCase enum name (used on
 * hydrate); `enumValue` is the numeric TeamChatPlatform sent on save (unambiguous
 * form binding). `label` is a proper noun — not translated.
 */
export const TEAM_CHAT_PLATFORMS: Array<{
  key: TeamChatPlatformKey;
  enumValue: number;
  label: string;
}> = [
  { key: 'slack', enumValue: 1, label: 'Slack' },
  { key: 'teams', enumValue: 2, label: 'Microsoft Teams' },
  { key: 'discord', enumValue: 3, label: 'Discord' },
  { key: 'googleChat', enumValue: 4, label: 'Google Chat' },
  { key: 'weCom', enumValue: 5, label: 'WeCom' },
  { key: 'mattermost', enumValue: 6, label: 'Mattermost' },
  { key: 'rocketChat', enumValue: 7, label: 'Rocket.Chat' },
];

export const createEmptyTeamChat = (): Record<
  TeamChatPlatformKey,
  TeamChatEntryForm
> =>
  TEAM_CHAT_PLATFORMS.reduce(
    (acc, p) => {
      acc[p.key] = { enabled: false, webhookUrl: '' };
      return acc;
    },
    {} as Record<TeamChatPlatformKey, TeamChatEntryForm>,
  );

export const DEFAULT_META_GRAPH_API_VERSION = 'v25.0';
export const DEFAULT_META_API_ENDPOINT = 'https://graph.facebook.com';

export const WHATSAPP_TRANSPORT_CHOICES: Array<{
  id: WhatsAppProviderType;
  name: string;
}> = [
  { id: 'twilio', name: 'resources.providers.whatsApp.variants.twilio' },
  { id: 'meta', name: 'resources.providers.whatsApp.variants.meta' },
];

export const MAX_PROVIDER_NAME_LENGTH = 100;

export const PROVIDER_STATUS_CHOICES: Array<{ id: ProviderStatus; name: string }> = [
  { id: 'active', name: 'resources.providers.status.active' },
  { id: 'inactive', name: 'resources.providers.status.inactive' },
];

export const EMAIL_PROVIDER_CHOICES: Array<{ id: EmailProviderVariant; name: string }> = [
  { id: 'emailSmtpViaBasicAuthProvider', name: 'resources.providers.email.variants.basicAuth' },
  { id: 'emailSmtpViaOauthProvider', name: 'resources.providers.email.variants.oauth2' },
  { id: 'api', name: 'resources.providers.email.variants.api' },
  { id: 'none', name: 'resources.providers.email.variants.none' },
];

export const EMAIL_SECURITY_CHOICES: Array<{ id: EmailSecurityType; name: string }> = [
  { id: 'none', name: 'NONE' },
  { id: 'tls', name: 'TLS' },
  { id: 'ssl', name: 'SSL' },
];

export const createEmptyProviderFormValues = (): ProviderFormValues => ({
  providerName: '',
  providerStatus: 'active',
  priority: 1,
  retries: 1,
  email: 'none',
  sms: false,
  whatsApp: false,
  whatsAppProviderType: 'twilio',
  telegram: false,
  viber: false,
  line: false,
  push: false,
  replacePushFile: false,
  emailSmtpViaBasicAuthProvider: {
    hostname: '',
    port: '',
    username: '',
    password: '',
    securityType: 'tls',
    from: '',
  },
  emailSmtpViaOauthProvider: {
    hostname: '',
    port: '',
    serviceAccountEmail: '',
    privateKey: '',
    securityType: 'tls',
    userToImpersonate: '',
  },
  emailApiProvider: {
    apiEndpoint: '',
    apiKey: '',
    from: '',
    customHeaders: '',
  },
  smsProvider: {
    apiEndpoint: '',
    apiKey: '',
    apisecret: '',
    senderId: '',
  },
  whatsappProvider: {
    apiEndpoint: '',
    apiKey: '',
    apisecret: '',
    businessPhoneNumber: '',
  },
  whatsappMetaProvider: {
    phoneNumberId: '',
    wabaId: '',
    accessToken: '',
    graphApiVersion: DEFAULT_META_GRAPH_API_VERSION,
    apiEndpoint: DEFAULT_META_API_ENDPOINT,
    metaBusinessPhoneNumber: '',
  },
  telegramProvider: {
    botToken: '',
  },
  viberProvider: {
    botToken: '',
    senderName: '',
  },
  lineProvider: {
    channelAccessToken: '',
  },
  webAppProviderModel: {
    fileContain: '',
    uploadFile: null,
  },
  teamChat: createEmptyTeamChat(),
});
